import React, { useRef, useState } from 'react';
import { type AlertColor, Box, Divider, Grid2 } from '@mui/material';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomCardContent from '@ui-components/Card/CustomCardContent';
import CustomCard from '@ui-components/Card/CustomCard';
import CustomTooltip from '@ui-components/ToolTip/CustomToolTip';
import CustomLink from '@ui-components/Link/CustomLink';
import CustomChip from '@ui-components/Chip/CustomChip';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import { useNavigate } from 'react-router-dom';
import VectorIcon from '@assets/images/Vector.svg';
import type { ConsultItem } from '../../API/services/caseManagerServices';
import type { CustomChipColor } from '@qler-view/shared_lib/src/shared/lib/ui-components/Chip/CustomChip.js';
import {
  CallStatusEnum,
  getChipDetails,
  callStatusToString,
} from '../components/ConsultStatusChip';
import ChatIcon from '@assets/images/chat.svg';
import CustomButton from '@qler-view/shared_lib/src/shared/lib/ui-components/Button/CustomButton';

import {
  CONSULT_MORE_DETAILS_ROUTE_PATH,
  CONSULT_WAITING_SCREEN_ROUTE_PATH,
} from '../../routers/RoutPath';
import ActivateIcon from '@assets/images/ActivateIcon.svg';
import ActivateConsultConfirmationPopup from './ActivateConsultConfirmationPopup';
import { useActivateConsult } from '../../API/hooks/caseManagerHook';
import { getUserFromStorage } from '../components/storageUtility';
import MinutesTimer from '../components/MinutesTimer';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import CustomBadge from '@qler-view/shared_lib/src/shared/lib/ui-components/Badge/CustomBadge';
import { formatFieldValue } from '@qler-view/shared_lib/src/reusableFunctions/CommonFunctions';
import TimeTicker from '@qler-view/shared_lib/src/shared/reusableComponents/TimeTicker';
import TimeDifference from '@qler-view/shared_lib/src/shared/reusableComponents/TimeDifference';
import CancelConsultModel, {
  type CancelReasonFacilityParams,
  type CancelReasonFacilitySubmitParams,
} from '@qler-view/shared_lib/src/shared/statusrequests/CancelConsultModel';
import { PORTAL_TYPES } from '@qler-view/shared_lib/src';
import {
  useCancelConsultMetadata,
  useSaveCancelConsult,
} from '../../shared/statusRequest/StatusRequestSetup';
import AscensionLogo from '@assets/images/ascension_logo.svg';
import { useResumeSelectedConsults } from '../../API/hooks/resumeConsultHook';

interface ConsultsProps {
  consultData: ConsultItem;
  assigned: boolean;
  onAccept?: () => void; // Callback function
  isAssigned: boolean;
  pastConsult?: boolean;
  isButtonRequired?: boolean;
  isRequestedByOthers: boolean;
}

export const getButtonLabel = (sessionStatus: string | null): string => {
  if (!sessionStatus) return '';

  switch (sessionStatus.toLowerCase()) {
    case CallStatusEnum.OnHold:
      return 'Activate';
    case CallStatusEnum.New:
      return 'Cancel';
    default:
      return '';
  }
};

const ConsultCard: React.FC<ConsultsProps> = ({
  consultData,
  assigned,
  onAccept,
  isAssigned,
  pastConsult,
  isButtonRequired,
  isRequestedByOthers,
}) => {
  const navigate = useNavigate();
  const user = getUserFromStorage();

  const consultStatus = consultData.sessionStatus?.toLowerCase();
  const MessageRef = useRef('');
  const [messageType, setMessageType] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const [openCancelRequest, setOpenCancelRequest] = useState(false);
  const { CancelConsultMetadata: ConsultCancelMetaData } =
    useCancelConsultMetadata(PORTAL_TYPES.FACILITY);
  const { mutate: cancelConsult } = useSaveCancelConsult();

  const getButtonVariant = () => {
    if (consultStatus === CallStatusEnum.OnHold) return 'contained';
    if (consultStatus === CallStatusEnum.New) return 'outlined';
    return 'contained';
  };

  const getButtonColor = () => {
    if (consultStatus === CallStatusEnum.OnHold) return 'success';
    if (consultStatus === CallStatusEnum.New) return 'error';
    return 'primary';
  };

  const [openConfirmationPopup, setOpenConfirmationPopup] =
    React.useState(false);

  const onSuccess = () => {
    onAccept?.();
    setOpenConfirmationPopup(false);
    setMessageType('success');
    MessageRef.current =
      'Consult Request has been successfully moved to the New status.';
    setOpenToast(true);
  };

  const { mutate: activate } = useActivateConsult(user?.userId ?? 0, onSuccess);

  const handleResumeSuccess = () => {
    navigate(
      `${CONSULT_WAITING_SCREEN_ROUTE_PATH}?consultId=${consultData.consultId}`,
      { replace: true }
    );
  };

  const { mutate: resumeConsult } =
    useResumeSelectedConsults(handleResumeSuccess);

  const getValueOrNA = (value: string | null) => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    return value;
  };

  const { color: chipColor, icon: chipIcon } = getChipDetails(
    consultStatus?.toString() as CallStatusEnum
  );

  const getResponsiveMaxLength = () => {
    const width = window.innerWidth;

    if (width < 600) return 30; // XS: mobile portrait
    if (width < 900) return 40; // SM: large mobile, small tablets
    if (width < 1200) return 50; // MD: tablets landscape, small laptops
    if (width < 1536) return 50; // LG: desktops
    return 60; // XL: large desktop monitors
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '…';
  };

  const CheckFOrStatusNew = (status?: string | null) => {
    if (
      status?.toLocaleLowerCase() !== CallStatusEnum.New ||
      status?.toLocaleLowerCase() !== CallStatusEnum.InProgress ||
      status?.toLocaleLowerCase() !== CallStatusEnum.OnHold
    ) {
      return true;
    }
    return false;
  };

  const newWaitTime = CheckFOrStatusNew(consultData.sessionStatus);

  const getCardHeader = () => {
    return (
      <Grid2 container>
        <Grid2 size={{ xs: 7, md: 7, lg: 7.4, xl: 7.2 }}>
          <Box className="text-align-left pb-4">
            <CustomTooltip
              id="name-tooltip"
              title={consultData.partnerName?.toString()}
              placement="bottom-end"
            >
              <CustomTypography
                variant="body3"
                id="HealthSystemName"
                color="textSecondary"
                noWrap={true}
              >
                {getValueOrNA(consultData.partnerName)}
              </CustomTypography>
            </CustomTooltip>
          </Box>
          <Box className="text-align-left">
            <CustomTooltip
              id="facilityname-tooltip"
              title={consultData.facilityName?.toString()}
              placement="bottom-end"
            >
              <CustomTypography variant="h5" id="FacilityName" noWrap={true}>
                {getValueOrNA(consultData.facilityName)}
              </CustomTypography>
            </CustomTooltip>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 5, md: 5, lg: 4.6, xl: 4.8 }}>
          <Box className="display-grid">
            <Box className="flex justify-end width-100">
              {consultData?.isExternalConsult !== null &&
                consultData?.isExternalConsult && (
                  <Box className="pr-8">
                    <AscensionLogo />
                  </Box>
                )}
              {consultData.sessionStatus.toLowerCase() === CallStatusEnum.New ||
              consultData.sessionStatus.toLowerCase() ===
                CallStatusEnum.InProgress ||
              consultData.sessionStatus.toLowerCase() ===
                CallStatusEnum.OnHold ? (
                <Box className={`flex clock-text }`}>
                  <TimeTicker createdOn={consultData.requestedOn} />
                </Box>
              ) : (
                <Box className={`flex clock-text ${newWaitTime ? '' : 'pb-4'}`}>
                  <TimeDifference
                    createdOn={consultData.requestedOn ?? ''}
                    waitEndTime={consultData.waitEndTime ?? ''}
                  />
                </Box>
              )}
            </Box>
            {!isAssigned && !pastConsult && (
              <Box className="float-right flex justify-end width-100">
                {consultData.acceptedOn && (
                  <CustomTypography variant="body3" id="assignedminsago">
                    <MinutesTimer targetTime={consultData.acceptedOn} />
                  </CustomTypography>
                )}
              </Box>
            )}
          </Box>
        </Grid2>
      </Grid2>
    );
  };

  const handleSubmitCancelReason = (data: CancelReasonFacilityParams) => {
    const mappedTechnicalDifficultiesId =
      [
        data.patientAdmittedId,
        data.noLongerRequiredId,
        data.patientDischargedId,
        data.technicalDifficultiesId,
      ].find((id) => id !== 0) ?? 0;

    const payload: CancelReasonFacilitySubmitParams = {
      cancelReasonId: data.cancelReasonId,
      technicalDifficultiesId: mappedTechnicalDifficultiesId,
      otherReason: data.otherReason,
    };
    cancelConsult(
      {
        userId: user?.userId.toString() ?? '',
        portalType: PORTAL_TYPES.FACILITY,
        consultId: consultData.consultId,
        ...payload,
      },
      {
        onSuccess: () => {
          setOpenToast(true);
          onAccept?.();

          setOpenCancelRequest(false);
        },
      }
    );
  };
  return (
    <Box>
      {openToast && (
        <CustomSnackbar
          id="login-toast"
          successOrError={messageType as AlertColor}
          open={openToast}
          handleClose={() => setOpenToast(false)}
          toastrMessage={MessageRef.current}
        />
      )}
      <Box>
        {openCancelRequest && (
          <CancelConsultModel
            openCancelConsultPopup={openCancelRequest}
            onCloseCancelConsultPopup={() => setOpenCancelRequest(false)}
            ConsultCancelMetaData={ConsultCancelMetaData}
            onSubmitCancelConsult={(values) => {
              handleSubmitCancelReason(values);
            }}
            portal={PORTAL_TYPES.FACILITY}
          />
        )}
      </Box>
      <Box>
        <ActivateConsultConfirmationPopup
          openPopup={openConfirmationPopup}
          onClosePopup={() => {
            setOpenConfirmationPopup(false);
          }}
          onConfirm={() => {
            activate({ consultId: consultData?.consultId });
          }}
        />
      </Box>
      <CustomCard
        id={`consult-Card-${consultData.consultId}`}
        className="consult-card"
      >
        <CustomCardContent
          id={`card-content-${consultData.consultId}`}
          className="consult-card-container"
        >
          {getCardHeader()}

          <Grid2 container>
            <Grid2 size={{ xs: 12 }}>
              <Box className="pt-18">
                <Divider />
              </Box>
            </Grid2>
          </Grid2>
          <Grid2 container>
            <Grid2 size={{ xs: 12 }}>
              <Box className="text-align-left pt-16 pb-8">
                <CustomTypography variant="h6" id="patientName">
                  {`${consultData.patientFirstName} ${consultData.patientLastName}`}
                </CustomTypography>
              </Box>
            </Grid2>
          </Grid2>
          <Grid2 container>
            <Grid2 size={{ xs: 4 }}>
              <Box
                className="text-align-left flex align-items-center pb-8"
                gap={0.5}
              >
                <CustomTypography
                  variant="body3"
                  id="dateofbirthTitle"
                  color="textSecondary"
                >
                  DOB:
                </CustomTypography>
                <CustomTypography
                  variant="subtitle2"
                  id="dateofbirth"
                  color="textPrimary"
                >
                  {formatFieldValue(consultData.dob)}
                </CustomTypography>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 4 }}>
              <Box
                className="text-align-left align-items-center flex pb-8"
                gap={0.5}
              >
                <CustomTypography
                  variant="body3"
                  id="mrnTitle"
                  color="textSecondary"
                >
                  MRN:
                </CustomTypography>
                <CustomTypography
                  variant="subtitle2"
                  id="mrnNumber"
                  color="textPrimary"
                >
                  {consultData.mrn || 'N/A'}
                </CustomTypography>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 4 }}>
              <Box
                className="text-align-left flex align-items-center pb-8"
                gap={0.5}
              >
                <CustomTypography
                  variant="body3"
                  id="unitsTitle"
                  color="textSecondary"
                >
                  ED/IP:
                </CustomTypography>
                <CustomTooltip
                  id="Reason-tooltip"
                  title={consultData.unitDepartment?.toString()}
                  placement="bottom-end"
                >
                  <CustomTypography
                    variant="subtitle2"
                    id="units"
                    color="textPrimary"
                  >
                    {truncateText(getValueOrNA(consultData.unitDepartment), 20)}
                  </CustomTypography>
                </CustomTooltip>
              </Box>
            </Grid2>
          </Grid2>
          <Grid2 container>
            <Grid2 size={{ xs: 6 }}>
              <Box
                className="text-align-left flex align-items-center pb-8"
                gap={0.5}
              >
                <CustomTypography
                  variant="body3"
                  id="callBackTitle"
                  color="textSecondary"
                >
                  Call Back:
                </CustomTypography>
                <CustomTooltip
                  id="Reason-tooltip"
                  title={consultData.callBackNumber?.toString()}
                  placement="bottom-end"
                >
                  <CustomTypography
                    variant="subtitle2"
                    id="callBackNumber"
                    color="textPrimary"
                  >
                    {truncateText(getValueOrNA(consultData.callBackNumber), 20)}
                  </CustomTypography>
                </CustomTooltip>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 6 }}>
              <Box
                className="text-align-left flex align-items-center pb-8"
                gap={0.5}
              >
                <CustomTypography
                  variant="body3"
                  id="cartTitle"
                  color="textSecondary"
                >
                  Cart:
                </CustomTypography>
                <CustomTypography
                  variant="subtitle2"
                  id="cart"
                  color="textPrimary"
                >
                  {getValueOrNA(consultData.cart)}
                </CustomTypography>
              </Box>
            </Grid2>
          </Grid2>
          <Grid2 container>
            <Grid2 size={{ xs: 12 }}>
              <Box
                className="text-align-left align-items-center flex pb-8"
                gap={0.5}
              >
                <CustomTypography
                  variant="body3"
                  id="reasonTitle"
                  color="textSecondary"
                >
                  Reason:
                </CustomTypography>
                <CustomTooltip
                  id="Reason-tooltip"
                  title={consultData.reason?.toString()}
                  placement="bottom-end"
                >
                  <CustomTypography
                    variant="subtitle2"
                    id="reason"
                    color="textPrimary"
                    noWrap={true}
                  >
                    {truncateText(
                      getValueOrNA(consultData.reason),
                      getResponsiveMaxLength()
                    )}
                  </CustomTypography>
                </CustomTooltip>
              </Box>
            </Grid2>
          </Grid2>
          <Grid2 container>
            <Grid2 size={{ xs: 12 }}>
              <Box className="flex text-align-left item-center" gap={0.5}>
                <CustomLink
                  color="primary"
                  underline="hover"
                  variant="body3"
                  onClick={() => {
                    navigate(CONSULT_MORE_DETAILS_ROUTE_PATH, {
                      state: {
                        consultId: consultData.consultId,
                        requestedByOthers: isRequestedByOthers,
                        chatOpen: false,
                      },
                    });
                  }}
                  id="moredetailsLink"
                >
                  <span className="display-inline-flex align-items-center cursor-pointer">
                    More Details&nbsp;
                    <VectorIcon />
                  </span>
                </CustomLink>
              </Box>
            </Grid2>
          </Grid2>
          <Grid2 container className="align-items-center flex pt-16">
            <Grid2 size={{ xs: 6 }}>
              <Box className="text-align-left" gap={0.5}>
                <CustomChip
                  id="statusChip"
                  color={chipColor as CustomChipColor}
                  className="chip-text-color"
                  label={callStatusToString[consultStatus as CallStatusEnum]}
                  icon={chipIcon}
                  iconPosition="left"
                  size="small"
                />
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 6 }}>
              {assigned === false ? (
                <Box
                  className="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  <Box className="icon-background-grey">
                    <CustomBadge
                      id={`${consultData.consultId}-chat-notification`}
                      badgeContent={consultData.unreadMessageCount}
                      color="error"
                      max={99}
                    >
                      <CustomIconButton
                        id="chatIcon"
                        icon={<ChatIcon />}
                        onClick={() => {
                          navigate(CONSULT_MORE_DETAILS_ROUTE_PATH, {
                            state: {
                              consultId: consultData.consultId,
                              requestedByOthers: isRequestedByOthers,
                              chatOpen: true,
                            },
                          });
                        }}
                      ></CustomIconButton>
                    </CustomBadge>
                  </Box>

                  {isButtonRequired ? (
                    <>
                      {consultStatus === CallStatusEnum.New &&
                        !consultData.isCustomUrl && (
                          <Box className="ml-8">
                            <CustomButton
                              id="btnJoin"
                              variant="contained"
                              color="primary"
                              size="large"
                              onClick={() => {
                                resumeConsult({
                                  userId: user?.userId ?? 0,
                                  consultId: Number(consultData.consultId),
                                });
                              }}
                            >
                              Join
                            </CustomButton>
                          </Box>
                        )}
                      {consultStatus === CallStatusEnum.OnHold && (
                        <Box className="ml-8">
                          <CustomButton
                            id="btnAction"
                            variant={getButtonVariant()}
                            color={getButtonColor()}
                            size="large"
                            startIcon={
                              consultStatus === CallStatusEnum.OnHold ? (
                                <ActivateIcon />
                              ) : null
                            }
                            onClick={() => {
                              if (consultStatus === CallStatusEnum.OnHold) {
                                setOpenConfirmationPopup(true);
                                return;
                              }
                              if (consultStatus === CallStatusEnum.New) {
                                setOpenCancelRequest(true);
                              }
                            }}
                          >
                            {getButtonLabel(consultData.sessionStatus ?? '')}
                          </CustomButton>
                        </Box>
                      )}
                    </>
                  ) : null}
                </Box>
              ) : (
                consultData.acceptedProviderId && (
                  <CustomTypography
                    variant="h6"
                    id="doctorName"
                    className="float-right"
                  >
                    <Box>
                      {`Dr. ${consultData.providerFirstName} ${consultData.providerLastName}`}
                    </Box>
                  </CustomTypography>
                )
              )}
            </Grid2>
          </Grid2>
        </CustomCardContent>
      </CustomCard>
    </Box>
  );
};

export default ConsultCard;
